import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, Building2, BookOpen, Coins, FileText, ExternalLink, ShieldCheck } from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import type { AdminOrganizationDetailResponse } from "@/src/api/types.gen";
import InvoiceModal from "@/components/checkout/InvoiceModal";

const STATUS_STYLES: Record<string, string> = {
    completed: "bg-emerald-50 text-emerald-700",
    pending: "bg-amber-50 text-amber-700",
    failed: "bg-red-50 text-red-700",
    active: "bg-emerald-50 text-emerald-700",
};

function formatMoney(amount: number, currency: string | null | undefined) {
    if (!currency) return String(amount);
    try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(
            amount,
        );
    } catch {
        return `${amount.toFixed(2)} ${currency}`;
    }
}

export function OrganizationSidebar({
    orgSlug,
    onClose,
}: {
    orgSlug: string | null;
    onClose: () => void;
}) {
    const [detail, setDetail] = useState<AdminOrganizationDetailResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fetchedForSlug, setFetchedForSlug] = useState<string | null>(null);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

    if (orgSlug !== null && orgSlug !== fetchedForSlug && !loading) {
        setLoading(true);
        setError(null);
        setDetail(null);
    }

    useEffect(() => {
        if (!orgSlug) return;

        api.getAdminPaymentsOrganizationApiV1AdminPaymentsDashboardOrganizationGet({
            query: { org_slug: orgSlug },
        })
            .then(({ data, error: apiErr }) => {
                if (apiErr || !data) {
                    throw new Error("Failed to load organization financial details.");
                }
                setDetail(data);
            })
            .catch((err) => setError(err instanceof Error ? err.message : "Lookup failed"))
            .finally(() => {
                setFetchedForSlug(orgSlug);
                setLoading(false);
            });
    }, [orgSlug]);

    if (!orgSlug) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex justify-end">
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs"
            />
            <div className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
                {/* Modal Header */}
                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 bg-slate-50/80">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
                            <Building2 className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-base font-bold text-slate-900">
                                    {detail?.companyName || detail?.name || orgSlug}
                                </h3>
                                <Link
                                    to={`/org/${orgSlug}/dashboard`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700"
                                    title="Open B2B Organization Dashboard"
                                >
                                    Portal <ExternalLink className="h-3 w-3" />
                                </Link>
                            </div>
                            <p className="text-xs text-slate-500 font-mono">slug: {orgSlug}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-2 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Sidebar Body */}
                <div className="flex-1 space-y-6 overflow-y-auto p-6">
                    {loading && (
                        <div className="space-y-4">
                            <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />
                            <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
                            <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />
                        </div>
                    )}

                    {error && (
                        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm font-semibold text-red-600">
                            {error}
                        </div>
                    )}

                    {!loading && !error && detail && (() => {
                        const revenueByCurrency = detail.revenueByCurrency || [];
                        const coursesPurchased = detail.coursesPurchased || [];
                        const transactions = detail.transactions || [];
                        const aiCreditsBalance = detail.aiCreditsBalance ?? 0;
                        const aiCreditsTotalPurchased = detail.aiCreditsTotalPurchased ?? 0;
                        const aiCreditsTotalConsumed = detail.aiCreditsTotalConsumed ?? 0;

                        return (
                        <>
                            {/* Overall Spend Breakdown by Currency */}
                            <div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                                    Lifetime Overall Spending
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {revenueByCurrency.length === 0 ? (
                                        <div className="col-span-full rounded-xl bg-slate-50 p-4 text-center text-xs text-slate-400">
                                            No paid transactions settled yet.
                                        </div>
                                    ) : (
                                        revenueByCurrency.map((entry) => (
                                            <div
                                                key={entry.currency}
                                                className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-xs"
                                            >
                                                <p className="text-[10px] font-bold uppercase text-slate-400">
                                                    {entry.currency} Total
                                                </p>
                                                <p className="text-lg font-bold text-slate-900 mt-0.5">
                                                    {formatMoney(entry.amount, entry.currency)}
                                                </p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Corporate Tax / Registration Credentials */}
                            {(detail.vatNumber || detail.crNumber || detail.companyName) && (
                                <div className="rounded-xl bg-slate-50 border border-slate-200/80 p-4 text-xs space-y-1.5">
                                    <div className="flex items-center gap-2 mb-2 font-bold text-slate-800">
                                        <ShieldCheck className="w-4 h-4 text-indigo-600" />
                                        Corporate Tax Registration
                                    </div>
                                    {detail.companyName && (
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">Legal Name:</span>
                                            <span className="font-medium text-slate-800">{detail.companyName}</span>
                                        </div>
                                    )}
                                    {detail.vatNumber && (
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">VAT TRN:</span>
                                            <span className="font-mono font-medium text-slate-800">{detail.vatNumber}</span>
                                        </div>
                                    )}
                                    {detail.crNumber && (
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">CR Number:</span>
                                            <span className="font-mono font-medium text-slate-800">{detail.crNumber}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* AI Credits Overview */}
                            <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Coins className="w-4 h-4 text-indigo-600" />
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-900">
                                            AI Generation Credits
                                        </h4>
                                    </div>
                                    <span className="text-xs font-bold text-indigo-700 font-mono">
                                        {aiCreditsBalance.toLocaleString()} available
                                    </span>
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-indigo-800">
                                    <div>Total Purchased: <span className="font-bold font-mono">{aiCreditsTotalPurchased.toLocaleString()}</span></div>
                                    <div>Total Consumed: <span className="font-bold font-mono">{aiCreditsTotalConsumed.toLocaleString()}</span></div>
                                </div>
                            </div>

                            {/* Courses & Seat Limits Purchased */}
                            <div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                                    <BookOpen className="w-3.5 h-3.5 text-slate-500" />
                                    Purchased Courses & Workspaces ({coursesPurchased.length})
                                </h4>
                                {coursesPurchased.length === 0 ? (
                                    <p className="text-xs text-slate-400 p-3 rounded-lg bg-slate-50 text-center">
                                        No courses assigned yet.
                                    </p>
                                ) : (
                                    <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white">
                                        {coursesPurchased.map((c, idx) => (
                                            <div key={idx} className="p-3.5 text-xs hover:bg-slate-50/60 transition flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                <div>
                                                    <p className="font-bold text-slate-900">{c.courseTitle}</p>
                                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                                        Workspace: <span className="font-medium text-slate-700">{c.workspaceName || c.workspaceSlug || "General"}</span>
                                                        {c.tierName ? ` · ${c.tierName}` : ""}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2 text-right">
                                                    {c.seatLimit && (
                                                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold text-[10px]">
                                                            {c.seatLimit} Seats
                                                        </span>
                                                    )}
                                                    {c.purchasedAt && (
                                                        <span className="text-[10px] text-slate-400 font-mono">
                                                            {new Date(c.purchasedAt).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Transaction Ledger Table */}
                            <div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                                    Organization Transactions ({transactions.length})
                                </h4>
                                {transactions.length === 0 ? (
                                    <p className="text-xs text-slate-400 p-3 rounded-lg bg-slate-50 text-center">
                                        No transactions on record.
                                    </p>
                                ) : (
                                    <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
                                        <table className="w-full text-left text-xs">
                                            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-100 uppercase tracking-wider">
                                                <tr>
                                                    <th className="py-2 px-3">Checkout ID / PO</th>
                                                    <th className="py-2 px-3">Item</th>
                                                    <th className="py-2 px-3">Amount</th>
                                                    <th className="py-2 px-3">Status</th>
                                                    <th className="py-2 px-3 text-right">Invoice</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 text-slate-700">
                                                {transactions.map((tx) => (
                                                    <tr key={tx.id} className="hover:bg-slate-50/50">
                                                        <td className="py-2 px-3">
                                                            <span className="font-mono text-slate-500 block truncate max-w-[120px]">
                                                                {tx.checkoutId}
                                                            </span>
                                                            {tx.purchaseOrderNumber && (
                                                                <span className="text-[10px] text-indigo-600 font-mono font-medium block">
                                                                    PO: {tx.purchaseOrderNumber}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="py-2 px-3">
                                                            <p className="font-medium text-slate-900">{tx.planName ?? "—"}</p>
                                                            <p className="text-[10px] text-slate-400">{tx.provider ?? ""}</p>
                                                        </td>
                                                        <td className="py-2 px-3 font-bold text-slate-900 font-mono">
                                                            {formatMoney(tx.amount, tx.currency)}
                                                        </td>
                                                        <td className="py-2 px-3">
                                                            <span
                                                                className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                                                    STATUS_STYLES[tx.status ?? ""] ?? "bg-slate-100 text-slate-600"
                                                                }`}
                                                            >
                                                                {tx.status ?? "unknown"}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-3 text-right">
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedInvoiceId(String(tx.id))}
                                                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
                                                            >
                                                                <FileText className="w-3 h-3" /> View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                        );
                    })()}
                </div>
            </div>

            {/* Tax Invoice Modal drilldown */}
            {selectedInvoiceId && (
                <InvoiceModal
                    identifier={selectedInvoiceId}
                    isOpen={Boolean(selectedInvoiceId)}
                    onClose={() => setSelectedInvoiceId(null)}
                />
            )}
        </div>,
        document.body,
    );
}
